import React, { useEffect, useState } from "react";
import { DrawerComponent, SkeletonComponent } from "@comps/components";
import { makeRequest, makeRequestStateless, notify } from "@utils/helpers";
import { updateCaseReq, getCaseDataReq } from "../requests";
import CaseJobForm from "./CaseJobForm";
import { checkUploadSize } from "@utils/mediaFormats";

// The file slots have no Form.Item to hang a message on, so a
// server-side rejection there would otherwise vanish silently. `files`
// is the whole-upload case: PHP refuses a request over post_max_size
// before Laravel routes it, and the handler reports it under that key.
const FILE_SLOTS = ['photos', 'xray', 'scans', 'files'];

const fileSlotErrors = (data) =>
  FILE_SLOTS.flatMap((slot) => (data && data[slot]) || []);

// Every message a validation failure (422) carries. The form shows them
// under their fields, but the field can be on another step or not on
// the form at all — refine_id has no control while editing — so the
// notice says them too, instead of just "Validation Error".
const validationMessages = (data, res) =>
  res?.code === 422 && data && typeof data === 'object'
    ? [...new Set(Object.values(data).flat().filter((m) => typeof m === 'string'))]
    : [];

const formName = "CaseStatuss";

const UpdateCase = (props) => {
  const [loader, setLoader] = useState(false);
  const [errors, setErrors] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [xray, setXray] = useState([]);
  const [scans, setScans] = useState([]);

  /*
   * The case's existing attachments are fetched before the drawer opens,
   * not after. The form used to load them on mount through the same
   * loader the footer buttons read, so opening Edit put "Working..." on
   * the Next button and the first step looked mid-submit. Owning the
   * fetch here also means the form gets its files as a prop and has one
   * less thing to do on mount.
   */
  const [caseFiles, setCaseFiles] = useState(null);

  // Stateless: `caseFiles === null` already means "still loading", and a
  // loader flag here would be the very thing that hijacked the buttons.
  const loadCaseFiles = () => {
    makeRequestStateless(getCaseDataReq, props.data.id, (res) => {
      setCaseFiles({
        photos: res.case_photos,
        xray: res.case_xrays,
        scans: res.case_scans,
      });
    }, () => setCaseFiles({ photos: [], xray: [], scans: [] }));
  };

  useEffect(() => {
    loadCaseFiles();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onFinish = (data) => {
    // Same guard as CreateCase — see the note there.
    const tooLarge = checkUploadSize({ photos, xray, scans });
    if (tooLarge) {
      notify(tooLarge.title, tooLarge.description, "error");
      return;
    }

    let payload = {
      "object": {
        id: props.data.id,
        ...data,
        photos: photos,
        xray: xray,
        scans: scans
      }
    }
    makeRequest(setLoader, updateCaseReq, payload, onSuccess, onError);
  }

  const onSuccess = (data, res) => {
    notify("Case updated", data.object?.case_number
      ? `Your changes to case ${data.object.case_number} are saved.`
      : res.msg);
    props.onUpdated(data.object);
  }

  /*
   * `res` matters as much as `data` here. This used to take only the
   * field errors, so a failure carrying no field at all — an upload
   * over the server's size limit, a timeout — set no message anywhere
   * and the drawer just sat there saying nothing, which is what "Case
   * not created, no reason given" looked like.
   */
  const onError = (data, res) => {
    // A failure that never reached the app (no connection, a refused
    // upload) carries no field errors at all, and the form reads this by
    // field name — handing it null took the whole page down.
    setErrors(data && typeof data === 'object' ? data : []);

    const rejected = fileSlotErrors(data);
    if (rejected.length) {
      notify("Those files weren't accepted", rejected.join(' '), "error");
      return;
    }

    const messages = validationMessages(data, res);
    notify("Case not saved", messages.length
      ? messages.join(' ')
      : res?.msg || "Something went wrong. Please try again.", "error");
  }

  /*
   * The drawer opens on the click that asked for it, showing the shape of
   * the form it is about to hold. Returning null until the attachments
   * landed meant pressing Edit appeared to do nothing at all — no drawer,
   * no spinner, nothing to say the click had registered.
   */
  if (caseFiles === null) {
    return (
      <DrawerComponent width={ 800 } mainTitle="Update" subTitle="Case" open={ true } footer={ false } onClose={ () => props.onClosed(false) }>
        <SkeletonComponent variant="form" rows={ 5 } actions />
      </DrawerComponent>
    );
  }

  return (
    <DrawerComponent width={ 800 } mainTitle="Update" subTitle="Case" open={ true } footer={ false } onClose={ () => props.onClosed(false) }>
      <CaseJobForm setLoader={ setLoader } loader={ loader } setErrors={ setErrors } errors={ errors } initialData={ props.data } onFinish={ onFinish } onClosed={ props.onClosed } formName={ formName } setPhotos={ setPhotos }
        setXray={ setXray } setScans={ setScans } caseFiles={ caseFiles } reloadCaseFiles={ loadCaseFiles } />
    </DrawerComponent>
  );
}

export default UpdateCase
